/* eslint-disable react-hooks/rules-of-hooks */
import { useSelector, useDispatch } from 'react-redux';

import * as redux from 'data/redux';
import * as module from './app';

const selectors = redux.selectors.app;
const actions = redux.actions.app;

/** Simple Selectors **/
export const usePageNumber = () => useSelector(selectors.pageNumber);
export const useFilters = () => useSelector(selectors.filters);
export const useEmailConfirmationData = () => useSelector(selectors.emailConfirmation);
export const useEnterpriseDashboardData = () => useSelector(selectors.enterpriseDashboard);
export const usePlatformSettingsData = () => useSelector(selectors.platformSettings);
export const useSelectSessionModalData = () => useSelector(selectors.selectSessionModal);
export const useSocialShareSettings = () => useSelector(selectors.socialShareSettings);

/** global-level meta-selectors **/
export const useHasCourses = () => useSelector(selectors.hasCourses);
export const useHasAvailableDashboards = () => useSelector(selectors.hasAvailableDashboards);
export const useShowSelectSessionModal = () => useSelector(selectors.showSelectSessionModal);

// eslint-disable-next-line
export const useCourseCardData = (selector) => (cardId) => useSelector(
  (state) => selector(state, cardId),
);
/** Course Card selectors (dormant: only reachable via the retired flat course list) **/
const { courseCard } = selectors;
export const useCardCertificateData = module.useCourseCardData(courseCard.certificate);
export const useCardCourseData = module.useCourseCardData(courseCard.course);
export const useCardCourseRunData = module.useCourseCardData(courseCard.courseRun);
export const useCardCreditData = module.useCourseCardData(courseCard.credit);
export const useCardEnrollmentData = module.useCourseCardData(courseCard.enrollment);
export const useCardEntitlementData = module.useCourseCardData(courseCard.entitlement);
export const useCardGradeData = module.useCourseCardData(courseCard.gradeData);
export const useCardProviderData = module.useCourseCardData(courseCard.courseProvider);
export const useCardRelatedProgramsData = module.useCourseCardData(courseCard.relatedPrograms);

export const useCardSocialSettingsData = (cardId) => {
  const socialShareSettings = module.useSocialShareSettings();
  const { socialShareUrl } = module.useCardCourseData(cardId);
  const defaultSettings = { isEnabled: false, shareUrl: '' };

  if (!socialShareSettings) {
    return { facebook: defaultSettings, twitter: defaultSettings };
  }
  const { facebook, twitter } = socialShareSettings;
  const loadSettings = (target) => ({
    isEnabled: target.isEnabled,
    shareUrl: `${socialShareUrl}?${target.utmParams}`,
  });
  return { facebook: loadSettings(facebook), twitter: loadSettings(twitter) };
};

export const useCardExecEdTrackingParam = (cardId) => {
  const { isExecEd2UCourse } = module.useCardEnrollmentData(cardId);
  const { authOrgId } = module.useEnterpriseDashboardData(cardId);
  return isExecEd2UCourse ? `?org_id=${authOrgId}` : '';
};

export const useTrackCourseEvent = (tracker, cardId, ...args) => {
  const { courseId } = module.useCardCourseRunData(cardId);
  return (e) => tracker(courseId, ...args)(e);
};

/** Nav selectors **/
export const useNavItems = () => useSelector(selectors.navItems);
export const useSelectedNavItem = () => useSelector(selectors.selectedNavItem);
export const useSelectedNavContent = () => useSelector(selectors.selectedNavContent);
export const useSelectedProductCategoryCards = () => useSelector(
  selectors.selectedProductCategoryCards,
);
export const useMostRecentCourseId = () => useSelector(selectors.mostRecentCourseId);

/** Events **/
export const useUpdateSelectSessionModalCallback = (cardId) => {
  const dispatch = useDispatch();
  return () => dispatch(actions.updateSelectSessionModal(cardId));
};

export const useSetPageNumber = () => {
  const dispatch = useDispatch();
  return (value) => dispatch(actions.setPageNumber(value));
};

export const useSetFilters = () => {
  const dispatch = useDispatch();
  return (value) => dispatch(actions.setFilters(value));
};

export const useAddFilter = () => {
  const dispatch = useDispatch();
  return (value) => dispatch(actions.addFilter(value));
};

export const useRemoveFilter = () => {
  const dispatch = useDispatch();
  return (value) => dispatch(actions.removeFilter(value));
};

export const useClearFilters = () => {
  const dispatch = useDispatch();
  return (value) => dispatch(actions.clearFilters(value));
};

export const useSelectNavItem = () => {
  const dispatch = useDispatch();
  return (navItem) => dispatch(actions.selectNavItem(navItem));
};

export const useLoadData = () => {
  const dispatch = useDispatch();
  return (data) => {
    dispatch(actions.loadLearnerDashboard(data));
  };
};

export default module;
